import { useState } from "react";
import { Link } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  FileText,
  Loader2,
  TrendingUp,
  ThumbsUp,
  Lightbulb,
  MessageSquare,
  ArrowRight,
  Sparkles,
  History,
  CheckCircle
} from "lucide-react";

const SCRIPT_TYPES = [
  { value: "cold_call", label: "Cold Call Script" },
  { value: "follow_up", label: "Follow-Up Script" },
  { value: "upsell", label: "Upsell Script" },
  { value: "objection_handling", label: "Objection Handling Script" },
  { value: "closing", label: "Closing Script" },
  { value: "voicemail", label: "Voicemail Script" },
  { value: "email", label: "Email Script" },
];

export default function ScriptAnalysis() {
  const [scriptText, setScriptText] = useState("");
  const [scriptType, setScriptType] = useState("cold_call");
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);

  const handleAnalyze = async () => {
    if (!scriptText.trim()) {
      toast.error("Please enter your script text");
      return;
    }

    setAnalyzing(true);
    setResult(null);

    try {
      const response = await fetch(`${API}/scripts/analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ script_text: scriptText, script_type: scriptType })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Analysis failed");
      }

      const data = await response.json();
      setResult(data);
      toast.success("Script analysis complete!");
    } catch (error) {
      toast.error("Failed to analyze script", { description: error.message });
    } finally {
      setAnalyzing(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-500";
    if (score >= 60) return "text-yellow-500";
    return "text-red-500";
  };

  return (
    <div className="max-w-5xl mx-auto space-y-8" data-testid="script-analysis-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">Script Analysis</h1>
          <p className="text-muted-foreground mt-1">
            Paste your sales script for AI-powered feedback and suggestions
          </p>
        </div>
        <Link to="/scripts/history">
          <Button variant="outline" data-testid="script-history-btn">
            <History className="w-4 h-4 mr-2" /> Past Analyses
          </Button>
        </Link>
      </div>

      {/* Input Area */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Your Script
          </CardTitle>
          <CardDescription>
            Paste the full text of your sales script below
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Script Type</Label>
            <Select value={scriptType} onValueChange={setScriptType}>
              <SelectTrigger data-testid="script-type-select" className="w-full sm:w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SCRIPT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Script Text</Label>
            <Textarea
              placeholder={"Hi [Name], this is [Your Name] from [Company].\n\nI'm reaching out because we've helped businesses like yours increase their leads by 40%...\n\nWould you have 15 minutes this week to discuss how we could do the same for you?"}
              value={scriptText}
              onChange={(e) => setScriptText(e.target.value)}
              rows={12}
              className="font-mono text-sm"
              data-testid="script-text-input"
            />
            <p className="text-xs text-muted-foreground text-right">
              {scriptText.length} characters
            </p>
          </div>

          <Button
            onClick={handleAnalyze}
            disabled={!scriptText.trim() || analyzing}
            size="lg"
            className="w-full sm:w-auto"
            data-testid="analyze-script-btn"
          >
            {analyzing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Analyzing Script...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Analyze Script
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <div className="space-y-6" data-testid="script-results">
          {/* Score Overview */}
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="relative overflow-hidden" data-testid="script-score-card">
              <div className={`absolute top-0 left-0 right-0 h-1 ${
                result.analysis.overall_score >= 80 ? "bg-green-500" :
                result.analysis.overall_score >= 60 ? "bg-yellow-500" : "bg-red-500"
              }`} />
              <CardContent className="p-6 text-center">
                <CheckCircle className={`w-10 h-10 mx-auto mb-3 ${getScoreColor(result.analysis.overall_score)}`} />
                <p className={`font-heading text-5xl font-bold ${getScoreColor(result.analysis.overall_score)}`}>
                  {result.analysis.overall_score}
                </p>
                <p className="text-muted-foreground mt-1">Script Score</p>
              </CardContent>
            </Card>

            <Card className="md:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Analysis Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground leading-relaxed">{result.analysis.summary}</p>
              </CardContent>
            </Card>
          </div>

          {/* Metrics */}
          {result.analysis.metrics && Object.keys(result.analysis.metrics).length > 0 && (
            <Card data-testid="script-metrics-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  Script Metrics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {Object.entries(result.analysis.metrics).map(([key, value]) => (
                    <div key={key} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium capitalize">{key.replace(/_/g, " ")}</span>
                        <span className={`font-mono font-bold ${getScoreColor(value)}`}>{value}%</span>
                      </div>
                      <Progress value={value} className="h-2" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Strengths & Improvements */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card data-testid="script-strengths-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-green-500">
                  <ThumbsUp className="w-5 h-5" /> Strengths
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {result.analysis.strengths?.map((s, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-500 mt-2 flex-shrink-0" />
                      <span className="text-sm">{s}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card data-testid="script-improvements-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-yellow-500">
                  <Lightbulb className="w-5 h-5" /> Areas to Improve
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {result.analysis.areas_for_improvement?.map((a, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <div className="w-2 h-2 rounded-full bg-yellow-500 mt-2 flex-shrink-0" />
                      <span className="text-sm">{a}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Line-by-Line Feedback */}
          {result.analysis.line_by_line_feedback?.length > 0 && (
            <Card data-testid="line-feedback-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-primary" />
                  Line-by-Line Feedback
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {result.analysis.line_by_line_feedback.map((item, i) => (
                  <div key={i} className="p-4 rounded-lg bg-muted/50 space-y-2">
                    <p className="text-sm italic text-muted-foreground">
                      &ldquo;{item.line}&rdquo;
                    </p>
                    <p className="text-sm font-medium">{item.feedback}</p>
                    {item.suggestion && (
                      <div className="text-sm text-primary bg-primary/5 rounded-md p-2 mt-1">
                        <span className="font-medium">Suggested:</span> {item.suggestion}
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Improved Version */}
          {result.analysis.improved_version && (
            <Card data-testid="improved-version-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  Improved Version
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="max-h-[400px]">
                  <pre className="text-sm whitespace-pre-wrap leading-relaxed font-sans">
                    {result.analysis.improved_version}
                  </pre>
                </ScrollArea>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => {
                    navigator.clipboard.writeText(result.analysis.improved_version);
                    toast.success("Improved script copied to clipboard!");
                  }}
                  data-testid="copy-improved-btn"
                >
                  Copy Improved Script
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Recommendations */}
          {result.analysis.recommendations?.length > 0 && (
            <Card data-testid="script-recommendations-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ArrowRight className="w-5 h-5 text-primary" />
                  Recommendations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {result.analysis.recommendations.map((rec, i) => (
                    <li key={i} className="flex items-center gap-3 p-3 rounded-lg bg-primary/5">
                      <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">
                        {i + 1}
                      </span>
                      <span className="text-sm">{rec}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
